import { useState } from 'react';
import { Ban, Loader2, Pencil } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { CurrencyInput } from '../ui/currency-input';
import { useToast } from '../../contexts/ToastContext';
import {
  useAdjustEntryFee,
  useCancelEntry,
  type PaymentStatus,
  type LedgerFeeBreakdown,
} from '../../api/extensions/useAdminEntries';

function formatCurrency(amount: number, currency = 'USD'): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
      amount,
    );
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** The three independently-adjustable fees on an entry. */
type FeeKey = 'entry' | 'transaction' | 'nonMember';

const FEE_FIELDS: ReadonlyArray<{
  key: FeeKey;
  label: string;
  hint: string;
}> = [
  { key: 'entry', label: 'Entry fee', hint: 'The event’s entry fee for this contestant.' },
  {
    key: 'transaction',
    label: 'Transaction fee',
    hint: 'Online payment surcharge. Leave alone to keep it calculated automatically.',
  },
  {
    key: 'nonMember',
    label: 'Non-member fee',
    hint: 'Charged because the contestant is not an active member.',
  },
];

type FeeDrafts = Record<FeeKey, string>;

const toDraft = (n: number) => (Number.isFinite(n) ? n.toFixed(2) : '0.00');

function parseDraft(value: string): number | null {
  if (value.trim() === '') return null;
  const n = parseFloat(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.max(0, round2(n));
}

interface EntryAdminActionsProps {
  registrationId: string;
  paymentStatus: PaymentStatus;
  isCancelled: boolean;
  /**
   * The parent RODEO was cancelled. Two consequences here: the backend lifts
   * its "entry is already in a draw" refusal, and it refunds the transaction
   * fee by default — the contestant didn't withdraw, the producer pulled the
   * event. Used to pre-arm the fee switch and to say so in the copy.
   */
  rodeoCancelled?: boolean;
  // Whether a payment ever cleared on this entry — gates the "also refund the
  // transaction fee" toggle on the cancel action.
  wasPaid: boolean;
  // Fee breakdown for the entry; when present the adjust editor pre-fills the
  // current entry fee. Absent on legacy rows — the editor then starts blank.
  feeBreakdown?: LedgerFeeBreakdown | null;
}

/**
 * Admin/delegate controls for adjusting an UNPAID entry's fees and for
 * cancelling an entry that is not yet in a draw. Sits in the entry detail
 * panel alongside MarkEntryPaidButton on both the Entries Ledger and the
 * user-details Entries tab.
 *
 * All three fees an entry carries — entry fee, transaction fee, non-member fee
 * — are independently adjustable. "Set all to $0" waives the whole entry in one
 * step, which is the common case when pulling an unpaid contestant.
 *
 * Client-confirmed workflows:
 *   - Drawn-out contestant, UNPAID → waive the fees here (the entry keeps its
 *     drawn slot; give it a DNP in live scoring), then add the $10 office fee
 *     as a manual ledger charge.
 *   - Removing BEFORE the draw → cancel the entry. Once it's in a draw the
 *     backend refuses the cancel (409) — waive to $0 + DNP instead.
 *
 * Adjusting fees is only offered while the entry is unpaid/free; a paid or
 * in-flight entry's fees are immutable (reduce with a refund). The server
 * re-checks paid state and guards the pay-just-before-edit race.
 *
 * Only fees the admin actually TOUCHED are submitted. Echoing an untouched
 * transaction fee back would pin a per-entry override and freeze the
 * automatically-calculated surcharge at today's value.
 */
export function EntryAdminActions({
  registrationId,
  paymentStatus,
  isCancelled,
  rodeoCancelled = false,
  wasPaid,
  feeBreakdown,
}: EntryAdminActionsProps) {
  const { showSuccess, showError } = useToast();
  const adjustFee = useAdjustEntryFee();
  const cancelEntry = useCancelEntry();

  const [editingFee, setEditingFee] = useState(false);
  const [drafts, setDrafts] = useState<FeeDrafts>({
    entry: '',
    transaction: '',
    nonMember: '',
  });
  const [dirty, setDirty] = useState<Set<FeeKey>>(new Set());
  const [confirmingWaiveAll, setConfirmingWaiveAll] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  // `null` = the admin hasn't touched the switch, so let the SERVER decide.
  // That matters because the server refunds the transaction fee by default
  // when the whole rodeo was cancelled (the contestant didn't withdraw — the
  // producer pulled the event), and sending a hard `false` from here would
  // silently override that and make them eat the processor's cut.
  const [refundTxnFee, setRefundTxnFee] = useState<boolean | null>(null);

  const feeEditable =
    !isCancelled &&
    (paymentStatus === 'unpaid' || paymentStatus === 'free');
  const cancelable = !isCancelled;

  if (!feeEditable && !cancelable) return null;

  const current: Record<FeeKey, number> = {
    entry: feeBreakdown?.entry_fee || 0,
    transaction: feeBreakdown?.transaction_fee || 0,
    nonMember: feeBreakdown?.non_member_fee || 0,
  };

  const parsed: Record<FeeKey, number | null> = {
    entry: parseDraft(drafts.entry),
    transaction: parseDraft(drafts.transaction),
    nonMember: parseDraft(drafts.nonMember),
  };
  const allValid = FEE_FIELDS.every((f) => parsed[f.key] != null);
  const newTotal = allValid
    ? round2(
        (parsed.entry as number) +
          (parsed.transaction as number) +
          (parsed.nonMember as number),
      )
    : null;
  const hasChanges = dirty.size > 0;

  const setFee = (key: FeeKey, value: string) => {
    setDrafts((d) => ({ ...d, [key]: value }));
    setDirty((s) => new Set(s).add(key));
  };

  const startEditing = () => {
    setEditingFee(true);
    setDrafts({
      entry: toDraft(current.entry),
      transaction: toDraft(current.transaction),
      nonMember: toDraft(current.nonMember),
    });
    setDirty(new Set());
  };
  const cancelEditing = () => {
    setEditingFee(false);
    setDrafts({ entry: '', transaction: '', nonMember: '' });
    setDirty(new Set());
    setConfirmingWaiveAll(false);
  };

  // Zero every fee. All three are marked dirty on purpose — a fee that already
  // reads $0 because it auto-resolved to $0 still needs an explicit override
  // written, or it would silently come back the moment the entry fee changes.
  const stageWaiveAll = () => {
    setDrafts({ entry: '0.00', transaction: '0.00', nonMember: '0.00' });
    setDirty(new Set<FeeKey>(['entry', 'transaction', 'nonMember']));
  };

  const submitFee = async () => {
    if (!allValid || !hasChanges) return;
    try {
      const result = await adjustFee.mutateAsync({
        registrationId,
        ...(dirty.has('entry') ? { newEntryFee: parsed.entry as number } : {}),
        ...(dirty.has('transaction')
          ? { newTransactionFee: parsed.transaction as number }
          : {}),
        ...(dirty.has('nonMember')
          ? { newNonMemberFee: parsed.nonMember as number }
          : {}),
      });
      showSuccess(
        'Entry fees updated',
        result.payment_amount === 0
          ? 'All fees waived — this contestant owes $0. Add the office fee as a manual ledger charge if applicable, and give them a DNP in live scoring.'
          : `This contestant now owes ${formatCurrency(result.payment_amount)}.`,
      );
      cancelEditing();
    } catch (err: any) {
      showError('Could not adjust entry fees', err?.message || 'Please try again.');
    }
  };

  const submitCancel = async () => {
    try {
      await cancelEntry.mutateAsync({
        registrationId,
        issueRefund: true,
        // Only send it when the admin actually chose; otherwise inherit the
        // server default (which refunds it for a cancelled rodeo).
        refundTransactionFee:
          wasPaid && refundTxnFee !== null ? refundTxnFee : undefined,
      });
      showSuccess(
        'Entry cancelled',
        wasPaid
          ? 'The entry was cancelled and its payment refunded.'
          : 'The entry was cancelled.',
      );
      setConfirmingCancel(false);
      setRefundTxnFee(null);
    } catch (err: any) {
      showError('Could not cancel entry', err?.message || 'Please try again.');
    }
  };

  return (
    <div className="rounded-md border border-border bg-card/60 p-3 space-y-3">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Adjust entry
      </div>

      {feeEditable ? (
        editingFee ? (
          <div className="space-y-2.5">
            {FEE_FIELDS.map((field) => (
              <div
                key={field.key}
                className="flex items-start justify-between gap-2"
              >
                <span className="text-sm text-muted-foreground">
                  {field.label}
                  <span className="block text-xs text-muted-foreground/80">
                    {field.hint}
                  </span>
                </span>
                <div className="w-28 shrink-0">
                  <CurrencyInput
                    value={drafts[field.key]}
                    onChange={(v) => setFee(field.key, v)}
                    disabled={adjustFee.isPending}
                    aria-label={`New ${field.label.toLowerCase()} amount`}
                  />
                </div>
              </div>
            ))}

            <div className="flex items-center justify-between gap-2 border-t border-border/60 pt-2 text-sm font-medium">
              <span>Total</span>
              <span>{newTotal != null ? formatCurrency(newTotal) : '—'}</span>
            </div>

            {confirmingWaiveAll ? (
              <div className="rounded-md border border-border bg-muted/40 p-2.5 space-y-2">
                <p className="text-sm">
                  Waive all fees for this contestant?
                  <span className="block text-xs text-muted-foreground">
                    Entry, transaction and non-member fees all drop to $0, so
                    they owe nothing. The entry keeps its slot in the draw —
                    give them a DNP in live scoring, and add the office fee as a
                    manual ledger charge if one applies.
                  </span>
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      stageWaiveAll();
                      setConfirmingWaiveAll(false);
                    }}
                    disabled={adjustFee.isPending}
                  >
                    Set all to $0
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirmingWaiveAll(false)}
                    disabled={adjustFee.isPending}
                  >
                    Keep fees
                  </Button>
                </div>
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2"
                onClick={() => setConfirmingWaiveAll(true)}
                disabled={adjustFee.isPending || confirmingWaiveAll}
              >
                Set all to $0 (waive)
              </Button>
              <Button
                size="sm"
                onClick={submitFee}
                disabled={adjustFee.isPending || !allValid || !hasChanges}
              >
                {adjustFee.isPending ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : null}
                Save fees
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={cancelEditing}
                disabled={adjustFee.isPending}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={startEditing}>
              <Pencil className="h-4 w-4 mr-1.5" />
              Adjust fees
            </Button>
            <span className="text-xs text-muted-foreground">
              Unpaid only. Set all to $0 to waive a drawn-out contestant (keep
              their slot, give a DNP in scoring).
            </span>
          </div>
        )
      ) : null}

      {cancelable ? (
        <div className="border-t border-border/60 pt-3">
          {confirmingCancel ? (
            <div className="space-y-2.5">
              {wasPaid ? (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm">
                    Also refund the transaction fee
                    <span className="block text-xs text-muted-foreground">
                      {rodeoCancelled
                        ? 'On by default — this rodeo was cancelled, so the fee is not the contestant\u2019s to eat.'
                        : 'Off by default — the processing fee is normally non-refundable.'}
                    </span>
                  </span>
                  <Switch
                    checked={refundTxnFee ?? rodeoCancelled}
                    disabled={cancelEntry.isPending}
                    onCheckedChange={setRefundTxnFee}
                  />
                </div>
              ) : null}
              {rodeoCancelled ? (
                <p className="text-xs text-muted-foreground">
                  This rodeo is cancelled, so entries already placed in the
                  draw can be cancelled too — no DNP workaround needed.
                </p>
              ) : null}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm">
                  Cancel this entry
                  {wasPaid ? ' and refund its payment' : ''}?
                </span>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={submitCancel}
                  disabled={cancelEntry.isPending}
                >
                  {cancelEntry.isPending ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : null}
                  Confirm cancel
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setConfirmingCancel(false);
                    setRefundTxnFee(false);
                  }}
                  disabled={cancelEntry.isPending}
                >
                  Keep entry
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-start gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmingCancel(true)}
              >
                <Ban className="h-4 w-4 mr-1.5" />
                Cancel entry
              </Button>
              {/*
                Guidance for cancelling an entry already placed in a draw (the
                backend refuses those, so the admin does the remedy by hand).
                The correct remedy differs by whether money has cleared:
                  - No payment applied (pay-later/unpaid): the fee is still
                    adjustable, so waive it to $0 and mark DNP.
                  - Payment applied: the fee is immutable, so the entry fee must
                    be refunded from the Financials tab before marking DNP.
                The office-fee line is an "if applicable" note (there is no
                office-fee flag on an entry — it's a manual $10 ledger charge),
                worded for each payment state per the client workflow.
              */}
              <div className="flex-1 min-w-[12rem] space-y-1 text-xs text-muted-foreground">
                {/* A cancelled rodeo lifts the in-draw restriction entirely, so
                    the whole waive-to-$0-and-DNP workaround below is not just
                    unnecessary — it is wrong advice. There is no performance to
                    not-perform in, and nothing left to collect. */}
                {rodeoCancelled ? (
                  <p>
                    This rodeo is cancelled, so any entry can be cancelled —
                    including ones already in a draw. No DNP or fee-waive
                    workaround is needed.
                  </p>
                ) : (
                  <>
                    {wasPaid ? (
                      <p>
                        Pre-draw only. Entries already in a draw can&apos;t be
                        cancelled — since this entry already has a payment
                        applied, refund the entry fee via the Financials tab and
                        then mark DNP.
                      </p>
                    ) : (
                      <p>
                        Pre-draw only. Entries already in a draw can&apos;t be
                        cancelled — set all fees to $0 and mark DNP instead.
                      </p>
                    )}
                    {wasPaid ? (
                      <p>
                        If an office fee applies, refund the entry fee minus the
                        $10 office fee (so the contestant isn&apos;t charged an
                        extra transaction fee), then add both a $10 office-fee
                        charge and a matching $10 credit from the Financials
                        tab&apos;s manual ledger so the account stays balanced.
                      </p>
                    ) : (
                      <p>
                        If an office fee applies, add a $10 charge from the
                        Financials tab&apos;s manual ledger and mark it blocking.
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
